import {
  Component,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EventService } from '@services/event-service';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule, DatePipe } from '@angular/common';
import { IconDirective } from '../../directives/icon.directive';
import { AttendanceTimelineComponent } from '../attendance-timeline/attendance-timeline.component';
import {
  Anomaly,
  AnomalyKindEnum,
  AttendancePhoto,
  Identification,
  PhotoComment,
  Session,
  VerificationStatusEnum
} from 'src/app/queries/generated';
import { IdFromGidPipe } from '@pipes/id-from-gid.pipe';
import { SessionAttendanceStatusComponent } from '@components/session-attendance-status/session-attendance-status.component';

@Directive({ selector: '[lvImgBuffer]' })
export class ImgBufferDirective implements OnInit, OnDestroy {
  private readonly renderer = inject(Renderer2);
  private readonly el = inject(ElementRef);

  observer: MutationObserver;

  @HostListener('load') onLoad() {
    setTimeout(() => this.renderer.removeClass(this.nativeEl, 'is-loading'));
  }

  get nativeEl(): HTMLImageElement {
    return this.el.nativeElement;
  }

  ngOnInit() {
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
          this.renderer.addClass(this.nativeEl, 'is-loading');
        }
      }
    });

    this.observer.observe(this.nativeEl, { attributes: true });
  }

  ngOnDestroy() {
    this.observer.disconnect();
  }
}

@Component({
  selector: 'lv-session-review-attendance',
  templateUrl: 'session-review-attendance.component.html',
  styleUrls: ['session-review-attendance.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    TranslateModule,
    ImgBufferDirective,
    IconDirective,
    AttendanceTimelineComponent,
    DatePipe,
    IdFromGidPipe,
    SessionAttendanceStatusComponent
  ]
})
export class SessionReviewAttendanceComponent implements OnInit {
  private ngUnsubscribe: Subject<EventService> = new Subject();
  private events = inject(EventService);

  activeSession: Session;
  attendanceVerificationStatus: VerificationStatusEnum;
  hasFlaggedPhotos = false;
  attendancePhotos: AttendancePhoto[];
  anomalies: Anomaly[];

  selectedCommentIndex = 0;
  selectedPhotoIndex = -1;

  selectedAnomaly: Anomaly;
  selectedAnomalyIndex = -1;

  absenceCount = 0;
  assistanceCount = 0;

  @Input() sessions: Session[];

  get identification(): Identification {
    return this.activeSession.activeIdentification;
  }

  // Photo
  get selectedPhoto(): AttendancePhoto {
    return this.attendancePhotos[this.selectedPhotoIndex];
  }

  get photoCount(): number {
    return this.attendancePhotos.length;
  }

  get photoNumber(): number {
    return this.selectedPhotoIndex + 1;
  }

  get anomalyForCurrentPhoto(): Anomaly {
    return this.anomalies.find((anomaly) =>
      anomaly.photoComments.nodes.some((photoComment) => photoComment.attendancePhoto.id === this.selectedPhoto.id)
    );
  }

  get commentForCurrentPhoto(): PhotoComment {
    const photoComments = this.anomalyForCurrentPhoto?.photoComments?.nodes || [];

    return photoComments.find((photoComment) => photoComment.attendancePhoto.id === this.selectedPhoto.id);
  }

  ngOnInit() {
    if (this.sessions.length) {
      this.activeSession = this.sessions[0];
      this.attendanceVerificationStatus = this.activeSession.attendanceVerificationStatus;

      this.initializePhotos();
      this.initializeAnomalies();
    }

    this.initializeEventListener();
  }

  isFirstPhoto(): boolean {
    return this.selectedPhotoIndex === 0;
  }

  isLastPhoto(): boolean {
    return this.selectedPhotoIndex === this.photoCount - 1;
  }

  isFirstAnomaly(): boolean {
    return this.selectedAnomalyIndex === 0;
  }

  isLastAnomaly(): boolean {
    return this.selectedAnomalyIndex === this.anomalies.length - 1;
  }

  nextPhoto() {
    this.selectedPhotoIndex = Math.min(this.selectedPhotoIndex + 1, this.photoCount);
  }

  previousPhoto() {
    this.selectedPhotoIndex = Math.max(this.selectedPhotoIndex - 1, 0);
  }

  previousAnomaly() {
    this.selectedAnomalyIndex = Math.max(this.selectedAnomalyIndex - 1, 0);

    this.selectedAnomaly = this.anomalies[this.selectedAnomalyIndex];
    this.events.publish('anomaly:select', this.selectedAnomaly);

    this.selectPhotoFromAnomaly(this.selectedAnomaly);
  }

  nextAnomaly() {
    this.selectedAnomalyIndex = Math.min(this.selectedAnomalyIndex + 1, this.anomalies.length - 1);

    this.selectedAnomaly = this.anomalies[this.selectedAnomalyIndex];
    this.events.publish('anomaly:select', this.selectedAnomaly);

    this.selectPhotoFromAnomaly(this.selectedAnomaly);
  }

  private selectPhotoFromAnomaly(anomaly: Anomaly) {
    if (anomaly === undefined) {
      return;
    }

    const newPhoto = this.attendancePhotos.find(
      (photo) => photo.id === anomaly.photoComments.nodes[0].attendancePhoto.id
    );

    if (newPhoto.id !== this.selectedPhoto.id) {
      this.selectedPhotoIndex = this.attendancePhotos.indexOf(newPhoto);
    }
  }

  private initializePhotos() {
    this.attendancePhotos = this.sessions.flatMap((session) => session.attendancePhotos.nodes);
    this.selectedPhotoIndex = this.attendancePhotos.findIndex((photo) => photo.flagged === true);
    this.hasFlaggedPhotos = this.selectedPhotoIndex !== -1;
  }

  private initializeAnomalies() {
    this.anomalies = this.sessions.flatMap((session) => session.anomalies);
    this.absenceCount = this.anomalies.filter((anomaly) => anomaly.kind === AnomalyKindEnum.Absent).length;
    this.assistanceCount = this.anomalies.filter((anomaly) => anomaly.kind === AnomalyKindEnum.Assistance).length;

    const initialAnomaly = this.anomalies.find((anomaly) =>
      anomaly.photoComments.nodes.some((photoComment) => photoComment.attendancePhoto.id === this.selectedPhoto.id)
    );

    if (initialAnomaly) {
      this.setCurrentAnomaly(initialAnomaly);

      setTimeout(() => this.events.publish('anomaly:select', initialAnomaly));
    }
  }

  private initializeEventListener() {
    this.events
      .of('anomaly:select')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((anomaly: Anomaly) => {
        if (anomaly !== this.selectedAnomaly) {
          this.setCurrentAnomaly(anomaly);
        }
      });
  }

  private setCurrentAnomaly(anomaly: Anomaly) {
    this.selectedAnomaly = anomaly;
    this.selectPhotoFromAnomaly(anomaly);
    this.selectedAnomalyIndex = this.anomalies.indexOf(anomaly);
  }
}
